import { EntityCard } from '@src/components/entity-card/EntityCard';
import { Check, ChevronDown, FolderSearch, ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';
import { DockPointer } from '@src/navigation/DockPointer';
import { Button } from '@src/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@src/components/ui/tooltip';
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuShortcut,
  ContextMenuTrigger,
} from '@src/components/ui/context-menu';
import { openBrowseable } from './open';
import { useLocaleInfo } from '@src/contexts/locale-context';
import type { Browseable, BrowseableDragData, BrowseableTreeProps, RowKeyAction, ToolbarAction } from './types';
import { useBrowseableTree } from './useBrowseableTree';
import {
  collectDroppedEntries,
  hasBrowseableDrag,
  hasExternalFilesDrag,
  readBrowseableDrag,
  writeBrowseableDrag,
} from './drag';
import { subscribeRefresh } from './refresh-store';
import { TreeSelectionContext, type TreeSelectionApi } from './useTreeSelection';
import { useOpenTabHashes } from '@src/tabs/use-tab-manager';
import { RAIL_DIM_WHEN_CLOSED } from '@src/lib/utils';

/** The row that last had keyboard focus in this tree. A refresh can
 *  re-create that row (a list re-loads, a section appears above it), which
 *  drops focus to the page; the re-created row takes it back so F2 / Delete /
 *  ⌘C still reach it. Only on re-creation — a row that merely re-renders never
 *  takes focus from wherever the user moved it. */
const KeyRowContext = React.createContext<{ current: { id: string; at: number } | null }>({ current: null });
/** Only a re-creation soon after the row was focused takes focus back; later,
 *  a blurred page must not hand Delete to a row the user has moved away from. */
const KEY_ROW_REFOCUS_MS = 2000;

/** Does this row show the item the URL has open? */
function rowMatchesActive(
  node: Browseable,
  activePointer: DockPointer | null,
  activeResourcePointer: DockPointer | null | undefined,
  activeKey: string | null | undefined,
): boolean {
  return !!(
    // Stable-id match: a typeid URL (activeKey) selects this row even though its
    // `pointer` is the vfs form.
    (
      (node.selectionKey && activeKey && node.selectionKey === activeKey) ||
      // Pointer-string match: the original path (vfs leaf, list/folder roots, etc.).
      (node.pointer &&
        activePointer &&
        ((node.pointer.viewType === activePointer.viewType && node.pointer.pointer === activePointer.pointer) ||
          node.pointer.resourceVfsPath?.equals((activeResourcePointer ?? activePointer).resourceVfsPath)))
    )
  );
}

/** Walk a root's currently-visible (expanded) subtree in render order,
 *  collecting the selectable rows. Powers Shift-range + Cmd/Ctrl+A. */
function collectVisibleSelectable(root: Browseable, tree: ReturnType<typeof useBrowseableTree>): Browseable[] {
  const out: Browseable[] = [];
  const walk = (node: Browseable) => {
    if (node.selectable && node.selectionKey) out.push(node);
    if (tree.isExpanded(node.id)) for (const c of tree.getChildren(node.id)) walk(c);
  };
  // Roots themselves aren't selectable; start from their visible children.
  if (tree.isExpanded(root.id)) for (const c of tree.getChildren(root.id)) walk(c);
  return out;
}

/**
 * Generic Notion-like tree menu.
 *
 * Invariants (mirrors the design doc):
 *  - Clicking a row with `pointer !== null` navigates (via `onNavigate`) —
 *    it does NOT expand; expansion belongs to the chevron. A pointer-less
 *    parent row still expands on click (header rows stay usable).
 *  - Double-clicking a row expands it as well — click+dblclick together =
 *    show in the body AND expand in the tree. (On an already-selected
 *    renamable row, double-click enters inline rename instead.)
 *  - Clicking a toolbar button runs a side effect; it never navigates.
 *  - Clicking the chevron toggles expansion; it does not navigate.
 *  - Selection is derived from `activePointer` — never stored locally.
 *  - Expansion is ephemeral (local Set<string>).
 *  - When `activePointer` changes, every root that `ownsPointer` is asked for
 *    its ancestor chain and every ancestor is expanded.
 */
export function BrowseableTree(props: BrowseableTreeProps) {
  const {
    roots,
    activePointer,
    activeResourcePointer,
    activeKey,
    header,
    onNavigate,
    isLoading,
    error,
    emptyState,
    className = '',
    persistKey,
    defaultExpandedIds,
    hoverExpandMs,
    hoverSeenMs,
    levelFooter,
    mirrored = false,
  } = props;

  const tree = useBrowseableTree(roots, { persistKey, defaultExpandedIds });
  // Set of open-tab identities → rows backed by an open tab stay bright, the rest
  // dim (see BrowseableRow). Subscribed once here, passed down through the tree.
  const openTabHashes = useOpenTabHashes();
  const keyRowRef = useRef<{ id: string; at: number } | null>(null);
  const treeElementRef = useRef<HTMLDivElement | null>(null);
  const lastResolvedRef = useRef<string | null>(null);
  const [dragData, setDragData] = useState<BrowseableDragData | null>(null);

  // Multi-select (a second cursor, orthogonal to URL-first navigation). Null
  // when no provider wraps the tree (popover menu, or a navigator without a
  // bulkActions resolver) → the tree behaves exactly as before. Read via a ref
  // in the effects below so they key only on structure, not on the selection
  // api's identity (which changes on every selection mutation).
  const selection = useContext(TreeSelectionContext);
  const selectionRef = useRef<TreeSelectionApi | null>(selection);
  selectionRef.current = selection;

  // Keep the selection's per-root visible order in sync so Shift-range and
  // Cmd/Ctrl+A operate over what's actually on screen. Keyed on structure only
  // (roots + tree expansion/load state) — selection mutations don't change what's
  // visible, so they must not re-run this all-roots walk.
  useEffect(() => {
    const sel = selectionRef.current;
    if (!sel) return;
    for (const root of roots) {
      sel.setVisibleOrder(root.id, collectVisibleSelectable(root, tree));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roots, tree.state]);

  // "Show in <root>" moves the highlight to that root until the open item changes.
  const [revealRootId, setRevealRootId] = useState<string | null>(null);
  useEffect(() => setRevealRootId(null), [activePointer, activeResourcePointer, activeKey]);

  // The root that highlights the active item: the revealed one, else the first
  // (in display order) whose loaded rows hold it.
  const activeRootId = useMemo(() => {
    const has = (node: Browseable): boolean =>
      rowMatchesActive(node, activePointer, activeResourcePointer, activeKey) ||
      tree.getChildren(node.id).some(has);
    const reveal = roots.find((r) => r.id === revealRootId);
    return (reveal && has(reveal) ? reveal : roots.find((r) => r !== reveal && has(r)))?.id ?? null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roots, tree.state.loadStates, activePointer, activeResourcePointer, activeKey, revealRootId]);

  const { t } = useLingui();
  // "Show in <root>": a later root that also holds the active item. Expands
  // it there and moves the highlight. Offered on the highlighted row only.
  const revealAction = useMemo<ToolbarAction | null>(() => {
    const p = activeResourcePointer ?? activePointer;
    const from = roots.findIndex((r) => r.id === activeRootId);
    const target = p && from >= 0 ? roots.slice(from + 1).find((r) => r.ownsPointer(p)) : undefined;
    if (!target) return null;
    return {
      id: 'reveal',
      icon: <FolderSearch className="h-3 w-3" />,
      label: t`Show in ${target.label}`,
      showBusyIndicator: false,
      run: async () => {
        await tree.expandParentsForPointer(p, target.id);
        setRevealRootId(target.id);
        requestAnimationFrame(() =>
          treeElementRef.current
            ?.querySelector('[role="treeitem"][aria-selected="true"]')
            ?.scrollIntoView({ block: 'center' }),
        );
      },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roots, activeRootId, activePointer, activeResourcePointer, tree.expandParentsForPointer, t]);

  // Clear selection when the set of roots changes identity (scope/filter change
  // — root ids carry the filter signature — or the type list changes). Mirrors
  // HistoryModal's de-select-on-change.
  const rootIdsKey = roots.map((r) => r.id).join('|');
  useEffect(() => {
    selectionRef.current?.clear();
  }, [rootIdsKey]);

  const handleNavigate = useCallback(
    (pointer: DockPointer) => {
      onNavigate?.(pointer);
    },
    [onNavigate],
  );

  // Subscribe to external refresh signals (e.g. asset deleted from a hover
  // toolbar). The adapter that emits the signal owns the node id and decides
  // which subtree to invalidate.
  useEffect(
    () =>
      subscribeRefresh((nodeId) => {
        void tree.invalidate(nodeId);
      }),
    [tree],
  );

  // Auto-expand ancestors for the active resource. Most routes encode their
  // resource directly, so this falls back to the URL pointer. Canonical TypeId
  // routes can provide a separately resolved VFS cursor without replacing the
  // URL/navigation identity. Dedupe by both pointer values so
  // we don't re-walk on every render — but only mark as resolved once a leaf
  // was actually found, so we retry when `roots` populate later (e.g. async
  // adapter like useAssetTypes loads after initial render).
  useEffect(() => {
    const resourcePointer = activeResourcePointer ?? activePointer;
    if (!resourcePointer) return;
    const key = `${activePointer?.viewType ?? ''}::${activePointer?.pointer ?? ''}::${resourcePointer.viewType ?? ''}::${resourcePointer.pointer ?? ''}::${activeKey ?? ''}::${rootIdsKey}`;
    if (key === lastResolvedRef.current) return;
    void tree.expandParentsForPointer(resourcePointer).then((leaves) => {
      requestAnimationFrame(() => {
        // Resource identity crosses presentation routes: an editor pointer and
        // an Assets fs pointer can name the same VFS file. Prefer the first
        // matching resource row in DOM/root order, then retain the typeid-key
        // fallback for entity-addressed assets and finally the first path leaf.
        const resourceUri = resourcePointer.resourceVfsPath?.uri;
        const treeElement = treeElementRef.current;
        const el =
          (resourceUri && treeElement?.querySelector(`[data-resource-vfs="${CSS.escape(resourceUri)}"]`)) ||
          (activeKey && treeElement?.querySelector(`[data-selection-key="${CSS.escape(activeKey)}"]`)) ||
          (leaves[0] && treeElement?.querySelector(`[data-browseable-id="${CSS.escape(leaves[0].id)}"]`));
        if (!el) return;
        lastResolvedRef.current = key;
        el.scrollIntoView({ block: 'center' });
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePointer, activeResourcePointer, activeKey, tree.expandParentsForPointer]);

  if (isLoading) {
    return (
      <div className={`p-4 text-center text-xs text-muted-foreground ${className}`}>
        <Trans>Loading...</Trans>
      </div>
    );
  }

  if (error) {
    return <div className={`p-4 text-center text-xs text-destructive ${className}`}>{error}</div>;
  }

  if (roots.length === 0) {
    return (
      <div className={`p-4 text-center ${className}`}>
        {/* The root level can still take a first row — an empty tree is
            exactly when its "add here" matters most. */}
        {levelFooter?.('', mirrored)}
        {emptyState ?? (
          <p className="text-xs text-muted-foreground">
            <Trans>No items</Trans>
          </p>
        )}
      </div>
    );
  }

  return (
    <div ref={treeElementRef} className={`flex h-full flex-col ${className}`} role="tree" aria-label={header?.title}>
      {header && (
        <div className="flex items-center gap-1 border-b p-1.5">
          <span className="text-xs font-medium text-muted-foreground">{header.title}</span>
          <div className="ms-auto flex items-center gap-0.5">
            {header.toolbar?.map((a) => (
              <ToolbarButton key={a.id} action={a} />
            ))}
            {header.extra}
          </div>
        </div>
      )}
      <div className="flex-1 space-y-0.5 overflow-auto p-1">
        <KeyRowContext.Provider value={keyRowRef}>
        {levelFooter?.('', mirrored)}
        {roots.map((root) => (
          <BrowseableRow
            key={root.id}
            node={root}
            level={0}
            rootId={root.id}
            hoverExpandMs={hoverExpandMs}
            hoverSeenMs={hoverSeenMs}
            mirrored={mirrored}
            levelFooter={levelFooter}
            tree={tree}
            selection={selection}
            activePointer={activePointer}
            activeResourcePointer={activeResourcePointer}
            activeKey={activeKey}
            activeRootId={activeRootId}
            revealAction={revealAction}
            openTabHashes={openTabHashes}
            onNavigate={handleNavigate}
            dragData={dragData}
            onDragStart={setDragData}
            onDragEnd={() => setDragData(null)}
          />
        ))}
        </KeyRowContext.Provider>
      </div>
    </div>
  );
}

/** One hover dwell on a row: `start` schedules `run` after `ms` (a no-op when
 *  `ms` is undefined, which is how every non-menu navigator opts out), `cancel`
 *  drops a pending one. Unmount cancels itself, so a caller adding a dwell
 *  never has to remember the cleanup. */
function useDwell(ms: number | undefined, run: () => void) {
  const timer = useRef<ReturnType<typeof setTimeout>>();
  const latest = useRef(run);
  latest.current = run;
  const cancel = useCallback(() => clearTimeout(timer.current), []);
  const start = useCallback(() => {
    if (!ms) return;
    clearTimeout(timer.current);
    timer.current = setTimeout(() => latest.current(), ms);
  }, [ms]);
  useEffect(() => cancel, [cancel]);
  return useMemo(() => ({ start, cancel }), [start, cancel]);
}

interface RowProps {
  node: Browseable;
  level: number;
  /** Id of the top-level root this row lives under — the multi-select scope. */
  rootId: string;
  tree: ReturnType<typeof useBrowseableTree>;
  selection: TreeSelectionApi | null;
  activePointer: DockPointer | null;
  activeResourcePointer?: DockPointer | null;
  activeKey?: string | null;
  /** Root that owns the highlight when the active item appears under several. */
  activeRootId: string | null;
  /** "Show in <later root>" — rendered on the highlighted row only. */
  revealAction: ToolbarAction | null;
  /** Open-tab identities (`pointer.tabHash`) → un-dimmed rows. */
  openTabHashes: Set<string>;
  onNavigate: (p: DockPointer) => void;
  dragData: BrowseableDragData | null;
  onDragStart: (data: BrowseableDragData) => void;
  onDragEnd: () => void;
  /** Dwell (ms) before hover expands this row. Undefined ⇒ nothing is
   *  scheduled ⇒ ordinary navigators never expand on hover. */
  hoverExpandMs?: number;
  /** Dwell (ms) before hover fires this row's `onHoverSeen`. Undefined ⇒ never. */
  hoverSeenMs?: number;
  levelFooter?: (parentId: string, mirrored: boolean) => React.ReactNode;
  /** Flip the direction cues for a leftward-growing menu — see BrowseableTreeProps. */
  mirrored?: boolean;
}

function BrowseableRow({
  node,
  level,
  rootId,
  tree,
  selection,
  activePointer,
  activeResourcePointer,
  activeKey,
  activeRootId,
  revealAction,
  openTabHashes,
  onNavigate,
  dragData,
  onDragStart,
  onDragEnd,
  hoverExpandMs,
  hoverSeenMs,
  levelFooter,
  mirrored = false,
}: RowProps) {
  const { t } = useLingui();
  const expanded = tree.isExpanded(node.id);
  const loadState = tree.getLoadState(node.id);
  const children = tree.getChildren(node.id);
  const [isDropTarget, setIsDropTarget] = useState(false);
  const [isDropping, setIsDropping] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const canRename = !!node.onRename && !node.content;
  const keyRow = useContext(KeyRowContext);
  const takesKeys = !!(node.keyActions || canRename);
  const rowRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const k = keyRow.current;
    if (
      takesKeys &&
      k?.id === node.id &&
      Date.now() - k.at < KEY_ROW_REFOCUS_MS &&
      document.activeElement === document.body
    ) {
      rowRef.current?.focus({ preventScroll: true });
    }
    // Mount only: this is the re-created row taking back its own focus.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startRename = useCallback(() => {
    if (!canRename) return;
    setDraft(node.label);
    setEditing(true);
  }, [canRename, node.label]);

  // Restored expansion (persistKey / defaultExpandedIds) marks nodes expanded
  // without the children fetch that interactive expand() runs — load on first
  // render in that state so restored layers aren't empty.
  useEffect(() => {
    if (expanded && loadState.status === 'idle' && node.listChildren) {
      void tree.loadChildren(node);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expanded, loadState.status, node.id]);

  // One highlight per tree: when the open item shows up under several roots
  // (Documents and Files), only the first root that has it lights up.
  const isSelected =
    activeRootId === rootId &&
    rowMatchesActive(node, activePointer, activeResourcePointer, activeKey);

  // Dim openable rows that aren't currently open as a tab (and aren't the active
  // row, which is open by definition). Hover restores full brightness. Rows
  // without a pointer (headers/categories) are never dimmed.
  const hasOpenTab = !!(node.pointer?.tabHash && openTabHashes.has(node.pointer.tabHash));
  const dimmed = !!node.pointer && !isSelected && !hasOpenTab;

  const hasChildrenHint = node.hasChildren === true || (node.hasChildren === 'unknown' && !!node.listChildren);

  const canSelect = !!(selection && node.selectable && node.selectionKey);
  const multiSelected = canSelect && selection.isSelected(node.selectionKey);

  const handleRowClick = useCallback(
    (e: React.MouseEvent) => {
      // Inline rename in progress — a click commits via the input's own handlers;
      // never navigate/toggle underneath it.
      if (editing) return;

      // Multi-select gestures take precedence on selectable rows. A modifier
      // click only mutates the selection — it never navigates or toggles
      // expansion. A plain click clears the set + primes the range anchor, then
      // falls through to the normal navigate/expand behavior below.
      if (canSelect) {
        // Cmd (mac) or Ctrl (win/linux) toggles; matches the modifier convention
        // used across the app's keyboard handlers.
        const mod = e.metaKey || e.ctrlKey;
        if (mod || e.shiftKey) {
          e.preventDefault();
          if (e.shiftKey) selection.selectRange(node, rootId);
          else selection.toggle(node, rootId);
          return;
        }
        selection.anchorAndClear(node, rootId);
      }

      // Label click NAVIGATES only — expansion belongs to the chevron (or a
      // double-click). A pointer-less parent still expands on click so header
      // rows (pointer: null) stay usable.
      if (!openBrowseable(node, onNavigate) && hasChildrenHint) {
        explicitToggle();
      }
    },
    [editing, canSelect, selection, rootId, hasChildrenHint, node, tree, onNavigate],
  );

  // Double-click: inline rename on a *selected* renamable row; otherwise
  // expand — combined with the single click that already fired, a double
  // click both shows the content (right pane) AND expands (left pane).
  const handleDoubleClick = useCallback(() => {
    if (canRename && isSelected) {
      startRename();
      return;
    }
    if (hasChildrenHint) {
      void tree.toggleExpand(node);
    }
  }, [canRename, isSelected, startRename, node, hasChildrenHint, tree]);

  /** The rows an action applies to: the whole multi-selection when this row is
   *  part of one, else just this row. */
  const rowsForAction = useCallback(
    (): Browseable[] => (multiSelected && selection && selection.count > 1 ? selection.selectedNodes : [node]),
    [multiSelected, selection, node],
  );

  const handleRowKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (editing || e.target !== e.currentTarget) return;
      const mod = e.metaKey || e.ctrlKey;
      let action: RowKeyAction | null = null;
      if (e.key === 'F2') {
        if (!canRename) return;
        e.preventDefault();
        startRename();
        return;
      }
      if (e.key === 'Enter') {
        e.preventDefault();
        if (!openBrowseable(node, onNavigate) && hasChildrenHint) void tree.toggleExpand(node);
        return;
      }
      if (e.key === 'Delete' || (e.key === 'Backspace' && mod)) action = 'delete';
      else if (mod && !e.shiftKey && !e.altKey) {
        const k = e.key.toLowerCase();
        if (k === 'c') action = 'copy';
        else if (k === 'x') action = 'cut';
        else if (k === 'v') action = 'paste';
        else if (k === 'd') action = 'duplicate';
      }
      const run = action ? node.keyActions?.[action] : undefined;
      if (!run) return;
      e.preventDefault();
      e.stopPropagation();
      // Deleted rows can't stay selected (the selection bar would count ghosts).
      void Promise.resolve(run(rowsForAction())).then(() => {
        if (action === 'delete') selection?.clear();
      });
    },
    [editing, canRename, startRename, node, onNavigate, hasChildrenHint, tree, rowsForAction, selection],
  );

  const commitRename = useCallback(async () => {
    setEditing(false);
    const next = draft.trim();
    if (next && next !== node.label) await node.onRename?.(next);
  }, [draft, node]);

  // The row's two hover dwells (menu mode). Hover-expand only ever EXPANDS —
  // never toggles: the pointer rests on a row it just expanded, so a toggle
  // would collapse it again and flicker. Collapse stays chevron / click /
  // double-click. Hover-seen stamps "the user looked at this" without opening
  // it; it re-fires on every qualifying dwell, so the stamp must be idempotent
  // (Bookmark.markSeen is).
  const expandDwell = useDwell(hoverExpandMs, () => void tree.expand(node));
  const seenDwell = useDwell(hoverSeenMs, () => node.onHoverSeen?.());
  // An explicit collapse latches hover off until the pointer leaves and comes
  // back. Without it, collapsing via the chevron re-expands ~150ms later — the
  // chevron lives inside the row, so collapsing it never ends the hover.
  const suppressHoverExpand = useRef(false);

  const handleRowPointerEnter = useCallback(
    (e: React.PointerEvent) => {
      if (e.pointerType !== 'mouse') return;
      seenDwell.start();
      if (hasChildrenHint && !expanded && !suppressHoverExpand.current) expandDwell.start();
    },
    [expandDwell, seenDwell, hasChildrenHint, expanded],
  );

  const handleRowPointerLeave = useCallback(() => {
    expandDwell.cancel();
    seenDwell.cancel();
    suppressHoverExpand.current = false;
  }, [expandDwell, seenDwell]);

  /** The one way to toggle from a deliberate user action. Latches hover-expand
   *  off as it goes: the pointer is still on the row you just collapsed, so
   *  without this the dwell would re-expand it ~150ms later. Any future
   *  explicit-collapse path (a keyboard ArrowLeft, a context menu) must come
   *  through here, or it silently reintroduces that. */
  const explicitToggle = useCallback(() => {
    suppressHoverExpand.current = true;
    void tree.toggleExpand(node);
  }, [node, tree]);

  const handleChevronClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      explicitToggle();
    },
    [explicitToggle],
  );

  // Intra-tree drags carry the full payload in lifted state → full canDrop
  // check during dragover. Foreign drags (started in another Browseable
  // surface, e.g. the desktop grid) only expose the MIME type during dragover
  // (HTML5 hides the body pre-drop) — accept optimistically on MIME presence
  // and run the full canDrop check at drop time via readBrowseableDrag.
  const canAcceptDrop = !!(dragData && node.onDrop && (!node.canDrop || node.canDrop(dragData)));
  const handleDragStart = useCallback(
    (e: React.DragEvent) => {
      if (!node.dragData) return;
      e.stopPropagation();
      writeBrowseableDrag(e, node.dragData);
      // Dragging a file out of the window onto the desktop saves it there
      // (Chromium / Electron). Ignored by drop targets inside the app.
      if (node.downloadUrl) {
        e.dataTransfer.setData('DownloadURL', `application/octet-stream:${node.label}:${node.downloadUrl}`);
        e.dataTransfer.effectAllowed = 'copyMove';
      }
      onDragStart(node.dragData);
    },
    [node.dragData, node.downloadUrl, node.label, onDragStart],
  );

  const handleDragEnd = useCallback(() => {
    setIsDropTarget(false);
    onDragEnd();
  }, [onDragEnd]);

  const handleDragOver = useCallback(
    (e: React.DragEvent) => {
      const foreign = !dragData && !!node.onDrop && hasBrowseableDrag(e);
      // OS files from outside the app (only the 'Files' type is visible
      // pre-drop) — a copy into the node, not a move within the tree.
      const external = !dragData && !!node.onExternalFilesDrop && hasExternalFilesDrag(e);
      if (!canAcceptDrop && !foreign && !external) return;
      e.preventDefault();
      e.stopPropagation();
      e.dataTransfer.dropEffect = (external && !canAcceptDrop && !foreign) || e.altKey ? 'copy' : 'move';
      setIsDropTarget(true);
    },
    [canAcceptDrop, dragData, node.onDrop, node.onExternalFilesDrop],
  );

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    if (e.currentTarget.contains(e.relatedTarget as Node | null)) return;
    setIsDropTarget(false);
  }, []);

  const handleDrop = useCallback(
    async (e: React.DragEvent) => {
      // Payload: lifted state for intra-tree drags, MIME body for foreign ones.
      const payload = dragData ?? readBrowseableDrag(e);
      if (!payload) {
        // No Browseable payload → an external OS drop. Entry handles expire
        // with the event, so collect synchronously before any await.
        if (!node.onExternalFilesDrop || !hasExternalFilesDrag(e)) return;
        e.preventDefault();
        e.stopPropagation();
        setIsDropTarget(false);
        setIsDropping(true);
        const collecting = collectDroppedEntries(e.dataTransfer);
        try {
          const entries = await collecting;
          if (entries.length) await node.onExternalFilesDrop(entries);
        } finally {
          setIsDropping(false);
          onDragEnd();
        }
        return;
      }
      if (!node.onDrop) return;
      if (node.canDrop && !node.canDrop(payload)) {
        setIsDropTarget(false);
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      setIsDropTarget(false);
      setIsDropping(true);
      // Alt/Option held at drop time ⇒ copy instead of move.
      const copy = e.altKey;
      try {
        await node.onDrop(payload, { copy });
      } finally {
        setIsDropping(false);
        onDragEnd();
      }
    },
    [dragData, node, onDragEnd],
  );

  /** Rows indent away from the side they start on, so the nesting reads as
   *  nesting whichever way the menu grows. */
  const indentSide = mirrored ? 'marginInlineEnd' : 'marginInlineStart';

  /**
   * Which way the "there is more this way" cues point — the collapsed chevron
   * and the row preview.
   *
   * Two independent facts decide it, so it is an XOR: the ambient reading
   * direction (Hebrew and Arabic ship in `src/locales`), and whether this tree
   * reversed its axis. Hardcoding either one alone gets the other case
   * backwards — which is what the LTR-only constants here did to every RTL
   * locale, in all of this component's consumers, before `mirrored` existed.
   */
  const cuePointsLeft = (useLocaleInfo().dir === 'rtl') !== !!mirrored;

  // Built once, wrapped conditionally below: the tooltip is the ONLY
  // difference between the two cases and the row is ~100 lines.
  const rowEl = (
    <div
      className={`group relative flex items-center gap-1 rounded-md p-1.5 text-xs transition-[color,background-color,border-color,opacity] ${
        mirrored ? 'flex-row-reverse' : ''
      } ${
        isSelected ? 'bg-accent font-medium text-accent-foreground' : 'hover:bg-muted'
      } ${dimmed ? RAIL_DIM_WHEN_CLOSED : ''} ${
        // Multi-select ring — distinct from, and composable with, the active
        // (bg-accent) fill: a row can be both the open editor and selected.
        multiSelected ? 'ring-2 ring-inset ring-primary' : ''
      } ${node.pointer || canSelect ? 'cursor-pointer' : 'cursor-default'} ${node.dragData ? 'active:cursor-grabbing' : ''} ${
        isDropTarget ? 'bg-primary/10 ring-1 ring-primary/40' : ''
      } ${isDropping ? 'opacity-60' : ''} ${node.rowClassName ?? ''}`}
      style={{ [indentSide]: `${level * 14}px` }}
      role="treeitem"
      aria-level={level + 1}
      aria-selected={isSelected}
      data-tag={node.tag}
      data-tag-kind={node.tag ? 'button' : undefined}
      data-multi-selected={multiSelected || undefined}
      aria-expanded={hasChildrenHint ? expanded : undefined}
      draggable={!!node.dragData}
      ref={rowRef}
      tabIndex={takesKeys ? -1 : undefined}
      onKeyDown={handleRowKeyDown}
      onFocus={(e) => {
        if (takesKeys && e.target === e.currentTarget) keyRow.current = { id: node.id, at: Date.now() };
      }}
      onPointerEnter={handleRowPointerEnter}
      onPointerLeave={handleRowPointerLeave}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={(e) => {
        void handleDrop(e);
      }}
    >
      <div
        className={`flex min-w-0 flex-1 items-center gap-1 overflow-hidden ${
          mirrored ? 'flex-row-reverse text-end' : ''
        }`}
      >
        {hasChildrenHint ? (
          <button
            type="button"
            onClick={handleChevronClick}
            className="flex h-4 w-4 flex-shrink-0 items-center justify-center"
            title={expanded ? t`Collapse` : t`Expand`}
            aria-label={expanded ? t`Collapse` : t`Expand`}
            data-testid={`browseable-chevron-${node.id}`}
          >
            {loadState.status === 'loading' ? (
              <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
            ) : expanded ? (
              <ChevronDown className="h-3 w-3 text-muted-foreground" />
            ) : cuePointsLeft ? (
              <ChevronLeft className="h-3 w-3 text-muted-foreground" />
            ) : (
              <ChevronRight className="h-3 w-3 text-muted-foreground rtl:-scale-x-100" />
            )}
          </button>
        ) : (
          <div className="w-4 flex-shrink-0" />
        )}

        <div
          className="flex min-w-0 flex-1 items-center gap-1.5 overflow-hidden"
          onClick={handleRowClick}
          onDoubleClick={handleDoubleClick}
        >
          {node.content ? (
            node.content
          ) : editing ? (
            <input
              autoFocus
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              onFocus={(e) => e.target.select()}
              onKeyDown={(e) => {
                e.stopPropagation();
                if (e.key === 'Enter') void commitRename();
                else if (e.key === 'Escape') setEditing(false);
              }}
              onBlur={() => void commitRename()}
              className="min-w-0 flex-1 rounded border border-input bg-background px-1 py-0.5 text-xs outline-none focus:ring-1 focus:ring-ring"
              data-testid={`browseable-rename-${node.id}`}
            />
          ) : (
            <>
              {node.icon}
              <span className="min-w-0 flex-1 truncate" title={node.tooltip || node.entity ? undefined : node.label}>
                {node.label}
              </span>
              {node.badge && <div className="flex-shrink-0">{node.badge}</div>}
            </>
          )}
        </div>
      </div>

      {/* The row's LAST flex child, so it sits on the edge OPPOSITE the chevron
          in both orientations (a mirrored row reverses it along with
          everything else). Its slot is always allocated and only its
          visibility changes on hover: nothing slides into the spot the pointer
          was aiming at, and a content-sized menu doesn't resize under it. The
          slot is text-height (`h-4`) and the buttons overflow it, so reserving
          it never makes the row taller. */}
      {isSelected && revealAction && <ToolbarButton action={revealAction} compact />}
      {node.toolbar && node.toolbar.length > 0 && (
        <div
          className="invisible flex h-4 flex-shrink-0 items-center gap-0.5 overflow-visible group-focus-within:visible group-hover:visible"
          data-testid={`browseable-row-toolbar-${node.id}`}
        >
          {node.toolbar.map((a) => (
            <ToolbarButton key={a.id} action={a} compact onStartRename={startRename} />
          ))}
        </div>
      )}
    </div>
  );

  // Right-click: the row's menu, built when it opens so live state (a
  // clipboard to paste) is current. Right-clicking a row outside the
  // multi-selection drops the selection, like every file manager.
  const [menuItems, setMenuItems] = useState<ToolbarAction[]>([]);
  // A tooltip only on a row without a right-click menu: wrapping the menu's row in a TooltipTrigger as
  // well takes over its focus and keys (F2 rename stops opening), and wrapping the ContextMenu ROOT hands a
  // ref to a function component. A menu row never showed its tooltip before either.
  const hasTip = !!(node.tooltip || node.entity) && !node.contextMenu;
  const tipTarget = hasTip ? <TooltipTrigger asChild>{rowEl}</TooltipTrigger> : rowEl;
  const triggerEl = node.contextMenu ? (
    <ContextMenu
      onOpenChange={(open) => {
        if (!open) return;
        if (canSelect && !multiSelected) selection.anchorAndClear(node, rootId);
        setMenuItems(typeof node.contextMenu === 'function' ? node.contextMenu() : (node.contextMenu ?? []));
      }}
    >
      <ContextMenuTrigger asChild>{rowEl}</ContextMenuTrigger>
      <ContextMenuContent
        className="min-w-[13rem]"
        data-testid={`browseable-context-menu-${node.id}`}
        // A closing menu hands focus back to the row it opened on — but never
        // out of a text field the user is typing in by then (a rename box).
        onCloseAutoFocus={(e) => {
          if (document.activeElement instanceof HTMLInputElement) e.preventDefault();
        }}
      >
        {menuItems.map((a, i) => {
          const disabled = typeof a.disabled === 'function' ? a.disabled() : !!a.disabled;
          return (
            <React.Fragment key={a.id}>
              {a.separatorBefore && i > 0 && <ContextMenuSeparator />}
              <ContextMenuItem
                disabled={disabled}
                className={`gap-2 text-xs ${a.destructive ? 'text-destructive focus:text-destructive' : ''}`}
                data-testid={`browseable-menu-${a.id}`}
                onSelect={() => {
                  // Let the menu close (and give focus back) before the rename input mounts.
                  if (a.startsRename) setTimeout(startRename, 0);
                  else
                    void Promise.resolve(a.run(rowsForAction())).then(() => {
                      if (a.destructive) selection?.clear();
                    });
                }}
              >
                <span className="[&>svg]:h-3.5 [&>svg]:w-3.5">{a.icon}</span>
                {a.label}
                {a.shortcut && <ContextMenuShortcut>{a.shortcut}</ContextMenuShortcut>}
              </ContextMenuItem>
            </React.Fragment>
          );
        })}
      </ContextMenuContent>
    </ContextMenu>
  ) : (
    tipTarget
  );

  return (
    <div
      data-browseable-id={node.id}
      data-selection-key={node.selectionKey}
      data-resource-vfs={node.pointer?.resourceVfsPath?.uri}
    >
      {hasTip ? (
        <Tooltip>
          {triggerEl}
          {/* Sideways, never below: rows stack vertically, so a bottom tooltip
              would cover the next row and sit in the pointer's downward travel
              path. WHICH side depends on the host — away from the panel, into
              open space; in a right-anchored menu "right" is off-screen.
              pointer-events-none: the tooltip portals to document.body, i.e.
              OUTSIDE the panel — without this, moving onto it fires the panel's
              pointerleave and closes a menu the user never left. The preview is
              read-only, so there is nothing in it to click. */}
          {node.entity ? (
            <TooltipContent side={cuePointsLeft ? 'left' : 'right'} className="pointer-events-none p-0">
              <EntityCard type={node.entity.type} id={node.entity.id} fallbackName={node.label} />
            </TooltipContent>
          ) : (
            <TooltipContent side={cuePointsLeft ? 'left' : 'right'} className="pointer-events-none max-w-xs">
              {node.tooltip}
            </TooltipContent>
          )}
        </Tooltip>
      ) : (
        triggerEl
      )}

      {expanded && (
        <div className="space-y-0.5">
          {/* This level's footer — new items file into `node.id`. FIRST row of
              the level, directly under the row that owns it, NOT last: a footer
              placed after the children lands after the whole expanded subtree,
              so N nested open levels stack N footers into one anonymous pile at
              the bottom of the panel with nothing to say which level each one
              belongs to (the bookmarks menu showed three). Leading the level
              binds each footer to its own row by position. Aligned with the
              children (same (level+1)*14 indent). */}
          {loadState.status === 'ready' && levelFooter && (
            <div style={{ [indentSide]: `${(level + 1) * 14}px` }}>{levelFooter(node.id, mirrored)}</div>
          )}
          {loadState.status === 'loading' && children.length === 0 && (
            <div className="p-1 text-xs text-muted-foreground" style={{ [indentSide]: `${(level + 1) * 14}px` }}>
              <Trans>Loading…</Trans>
            </div>
          )}
          {loadState.status === 'error' && (
            <div className="p-1 text-xs text-destructive" style={{ [indentSide]: `${(level + 1) * 14}px` }}>
              {loadState.message || t`Failed to load`}
            </div>
          )}
          {/* A levelFooter makes an empty folder actionable ("add here"), so
              the bare "Empty" label would just be noise beside it. */}
          {loadState.status === 'ready' && children.length === 0 && !levelFooter && (
            <div className="p-1 text-xs text-muted-foreground" style={{ [indentSide]: `${(level + 1) * 14}px` }}>
              <Trans>Empty</Trans>
            </div>
          )}
          {children.map((child) => (
            <BrowseableRow
              key={child.id}
              node={child}
              level={level + 1}
              rootId={rootId}
              hoverExpandMs={hoverExpandMs}
              hoverSeenMs={hoverSeenMs}
              mirrored={mirrored}
              levelFooter={levelFooter}
              tree={tree}
              selection={selection}
              activePointer={activePointer}
              activeResourcePointer={activeResourcePointer}
              activeKey={activeKey}
              activeRootId={activeRootId}
              revealAction={revealAction}
              openTabHashes={openTabHashes}
              onNavigate={onNavigate}
              dragData={dragData}
              onDragStart={onDragStart}
              onDragEnd={onDragEnd}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export function ToolbarButton({
  action,
  compact,
  onStartRename,
}: {
  action: ToolbarAction;
  compact?: boolean;
  /** Runs instead of `action.run` for a `startsRename` action. */
  onStartRename?: () => void;
}) {
  const { t } = useLingui();
  const [busy, setBusy] = useState(false);
  // A destructive action takes two clicks: the first arms it, the second runs.
  const [armed, setArmed] = useState(false);
  const showBusy = action.showBusyIndicator ?? true;

  const handleClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (action.startsRename) {
      onStartRename?.();
      return;
    }
    if (action.destructive && !armed) {
      setArmed(true);
      return;
    }
    setArmed(false);
    const result = action.run();
    if (result instanceof Promise) {
      if (showBusy) setBusy(true);
      try {
        await result;
      } finally {
        setBusy(false);
      }
    }
  };

  const label = armed ? t`Confirm: ${action.label}` : action.label;

  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      className={`${compact ? 'h-5 w-5' : 'h-6 w-6'} ${
        armed ? 'bg-destructive/10 text-destructive hover:bg-destructive/20 hover:text-destructive' : ''
      }`}
      onClick={(e) => void handleClick(e)}
      onPointerLeave={() => setArmed(false)}
      onBlur={() => setArmed(false)}
      onKeyDown={(e) => {
        if (e.key === 'Escape' && armed) {
          e.stopPropagation();
          setArmed(false);
        }
      }}
      disabled={busy}
      title={label}
      aria-label={label}
      data-armed={armed || undefined}
      data-testid={`browseable-toolbar-${action.id}`}
    >
      {busy ? (
        <Loader2 className="h-3 w-3 animate-spin" />
      ) : armed ? (
        <Check className="h-3 w-3" />
      ) : (
        <span className="[&>svg]:h-3 [&>svg]:w-3">{action.icon}</span>
      )}
    </Button>
  );
}
